import { create } from 'zustand';
import { authClient } from './api';

/**
 * Sensitive staff actions (viewing backup codes, turning two-step off, resetting someone's two-step...) re-ask for
 * the password, then stay unlocked for a while (A16.7 "Ask for password again before sensitive actions", 10 minutes
 * by default). Held in memory only: a reload asks again, which is the safe direction to fail.
 */
export const DEFAULT_REAUTH_MINUTES = 10;

interface ReauthState {
  confirmedAt: number | null;
  markConfirmed: (now?: number) => void;
  clear: () => void;
}

export const useReauthStore = create<ReauthState>((set) => ({
  confirmedAt: null,
  markConfirmed: (now = Date.now()) => set({ confirmedAt: now }),
  clear: () => set({ confirmedAt: null }),
}));

export function isReauthFresh(confirmedAt: number | null, minutes: number, now = Date.now()): boolean {
  return confirmedAt !== null && now - confirmedAt < minutes * 60_000;
}

export type PasswordCheck = { ok: true } | { ok: false; message: string };

/** Asks the API whether `password` is the signed-in user's password (POST /api/auth/verify-password). */
export async function verifyPassword(password: string): Promise<PasswordCheck> {
  const { error } = await authClient.$fetch('/verify-password', { method: 'POST', body: { password } });
  if (!error) return { ok: true };
  if (error.status === 401) return { ok: false, message: 'That isn’t your password.' };
  if (error.status === 429) return { ok: false, message: 'Too many tries. Wait a minute and try again.' };
  return { ok: false, message: 'Something went wrong. Please try again.' };
}

/**
 * The signed-in user's own backup codes (POST /api/auth/two-factor/view-backup-codes). With a `password` it checks
 * it first; without one the server only answers while this session's re-ask window is open (`reauth: true` when not).
 */
export async function viewBackupCodes(
  password?: string,
): Promise<{ codes: string[] } | { message: string; reauth?: boolean }> {
  const { data, error } = await authClient.$fetch<{ backupCodes: string[] }>('/two-factor/view-backup-codes', {
    method: 'POST',
    body: password ? { password } : {},
  });
  if (data) return { codes: data.backupCodes };
  const code = (error as { code?: string } | null)?.code;
  if (code === 'REAUTH_REQUIRED') return { message: 'Confirm your password to continue.', reauth: true };
  if (error?.status === 401) return { message: 'That isn’t your password.' };
  if (error?.status === 429) return { message: 'Too many tries. Wait a minute and try again.' };
  return { message: 'We couldn’t load your backup codes. Please try again.' };
}
